import type { Ring } from '../../../geo/types';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { gableRoof } from '../roofSolids';
import { boxSolid, horizontalCap, subFrame } from '../solids';
import { SURFACE } from '../../mesh/attributes';
import { PATTERN } from '../../../render/patterns/patternIds';
import { pointedArchRise, pointedWindow, roundArch } from '../panels';
import { buildRangeRoofGables, type RangeRoofGables } from './conventRoofGables';
import { corners } from './genericBlock';
import { edgePanel, openedWall, rectWall, type MasonryStyles } from './masonry';

export interface ConventRange {
  readonly length: number;
  readonly width: number;
  readonly eaves: number;
  readonly ridge: number;
  readonly floors: number;
  readonly bays: number;
  readonly passage: number;
  readonly passageU?: number;
  readonly blindSides?: readonly number[];
  readonly gableWindowSides?: readonly number[];
  readonly doorSide?: number;
  readonly roofGables?: RangeRoofGables;
  readonly windowRows?: readonly {
    readonly sill: number;
    readonly top: number;
    readonly round?: boolean;
    readonly pointed?: boolean;
    readonly width?: number;
  }[];
}

export function conventRangeRect(part: ConventRange) {
  return { u0: -part.length / 2, u1: part.length / 2, v0: -part.width / 2, v1: part.width / 2 };
}

export function buildConventRange(
  builder: MeshBuilder,
  frame: Frame,
  styles: MasonryStyles,
  part: ConventRange
): void {
  const rect = conventRangeRect(part),
    ring = corners(rect);
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % 4]!,
      end = side % 2 === 1,
      length = end ? part.width : part.length,
      bays = end ? 2 : part.bays,
      holes: Ring[] = [],
      passageOffset = (side === 0 ? 1 : -1) * (part.passageU ?? 0);
    const rows =
      part.windowRows ??
      Array.from({ length: part.floors }, (_, floor) => ({
        sill: 1.05 + floor * 2.8,
        top: 2.55 + floor * 2.8,
        round: false,
      }));
    for (let floor = 0; floor < rows.length; floor++)
      for (let bay = 0; bay < bays; bay++) {
        const x = ((bay + 0.5) * length) / bays,
          row = rows[floor]!,
          y = row.sill,
          width = 'width' in row && typeof row.width === 'number' ? row.width : 0.8;
        if (part.blindSides?.includes(side)) continue;
        if (part.doorSide === side && floor === 0 && bay === 0) {
          holes.push([
            [x - 0.45, 0.1],
            [x + 0.45, 0.1],
            [x + 0.45, 2.5],
            [x - 0.45, 2.5],
          ]);
          continue;
        }
        if (
          !end &&
          part.passage &&
          floor === 0 &&
          Math.abs(x - length / 2 - passageOffset) < part.passage / 2 + 0.5
        )
          continue;
        holes.push(
          'pointed' in row && row.pointed
            ? pointedWindow(x, y, width, row.top - pointedArchRise(width), 8)
            : row.round
              ? roundArch(x, y, width, row.top - width / 2, 8)
              : [
                  [x - width / 2, y],
                  [x + width / 2, y],
                  [x + width / 2, row.top],
                  [x - width / 2, row.top],
                ]
        );
      }
    if (!end && part.passage) {
      const panel = edgePanel(from, to),
        mid = length / 2 + passageOffset;
      // Keep tall lower windows whole where they rise above the passage lintel.
      openedWall(
        builder,
        styles,
        frame,
        panel,
        [
          [0, 0],
          [mid - part.passage / 2, 0],
          [mid - part.passage / 2, 2.7],
          [mid + part.passage / 2, 2.7],
          [mid + part.passage / 2, 0],
          [length, 0],
          [length, part.eaves],
          [0, part.eaves],
        ],
        holes
      );
    } else rectWall(builder, styles, frame, [from, to], [0, part.eaves], holes);
    if (part.doorSide === side) {
      const x = length / (2 * bays),
        doorFrame = subFrame(frame, from[0], from[1], Math.atan2(to[1] - from[1], to[0] - from[0]));
      boxSolid(
        builder,
        { ...styles.opening, surface: SURFACE.wood, pattern: PATTERN.timberFront1538 },
        doorFrame,
        [x - 0.44, 0.12, -0.18],
        [x + 0.44, 2.48, -0.13],
        { top: true, bottom: true }
      );
    }
    if (end)
      openedWall(
        builder,
        styles,
        frame,
        edgePanel(from, to),
        [
          [0, part.eaves],
          [length, part.eaves],
          [length / 2, part.ridge],
        ],
        part.blindSides?.includes(side) && !part.gableWindowSides?.includes(side)
          ? []
          : [
              [
                [length / 2 - 0.25, part.eaves + 0.35],
                [length / 2 + 0.25, part.eaves + 0.35],
                [length / 2 + 0.25, part.eaves + 1.1],
                [length / 2 - 0.25, part.eaves + 1.1],
              ],
            ]
      );
  });
  if (part.passage) {
    const half = part.passage / 2,
      centre = part.passageU ?? 0;
    rectWall(
      builder,
      styles,
      frame,
      [
        [centre - half, rect.v0],
        [centre - half, rect.v1],
      ],
      [0, 2.7]
    );
    rectWall(
      builder,
      styles,
      frame,
      [
        [centre + half, rect.v1],
        [centre + half, rect.v0],
      ],
      [0, 2.7]
    );
    horizontalCap(
      builder,
      { ...styles.wall, surface: SURFACE.wood, pattern: PATTERN.timberFront1538 },
      frame,
      corners({ ...rect, u0: centre - half, u1: centre + half }),
      2.7,
      false
    );
    horizontalCap(builder, styles.wall, frame, ring, 2.75, true);
  } else horizontalCap(builder, styles.wall, frame, ring, 0.01, true);
  if (part.roofGables) buildRangeRoofGables(builder, frame, styles, part, part.roofGables);
  else
    gableRoof(builder, styles, frame, {
      ...rect,
      eaveY: part.eaves,
      ridgeY: part.ridge,
      ends: { start: false, end: false },
    });
}
